import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react'
import { clsx } from 'clsx'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  fullWidth?: boolean
  children?: ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        style={variant === 'primary' ? { boxShadow: '0 0 30px rgba(46,107,255,0.3), 0 4px 20px rgba(46,107,255,0.25)' } : undefined}
        className={clsx(
          'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all',
          'focus-visible:outline-none',
          'disabled:pointer-events-none disabled:opacity-50',
          {
            'bg-cyan text-bg hover:opacity-90 active:scale-[0.98]': variant === 'primary',
            'bg-bg-card-light text-cyan border border-border': variant === 'secondary',
            'border border-border-cyan text-cyan bg-transparent': variant === 'outline',
            'bg-transparent text-cyan': variant === 'ghost',
            'bg-red text-white hover:opacity-90': variant === 'danger',
          },
          {
            'h-8 px-3 text-sm': size === 'sm',
            'h-11 px-4 text-sm': size === 'md',
            'h-13 px-6 text-base': size === 'lg',
          },
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin shrink-0"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
            <path
              d="M12 2a10 10 0 0 1 10 10"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
